/**
 * Pure reducer + helpers for useActiveSquads.
 *
 * Separated from useActiveSquads.ts so jest tests can import the reducer
 * without dragging in api.ts (which uses Vite-only `import.meta` syntax
 * that CRA's Babel preset cannot parse).
 */
import {
  Squad,
  SquadMember,
  SquadAuditEvent,
  SquadMemberStatus,
  TERMINAL_SQUAD_STATES,
  TERMINAL_MEMBER_STATES,
} from '../types/squads';


export interface SquadEntry {
  squad: Squad;
  members: SquadMember[];
  lastEventTs: string;
}

export interface ActiveSquadsState {
  bySquadId: Record<string, SquadEntry>;
}


// Backend bus PeerEvent shape — uses `target` (not `payload`).
export interface WsEventShape {
  event_type: string;
  timestamp?: string;
  target?: Record<string, any> | null;
}


export type Action =
  | { type: 'HYDRATE'; squads: Squad[]; members: Record<string, SquadMember[]> }
  | { type: 'WS_EVENT'; event: WsEventShape }
  | { type: 'REPLAY_AUDIT'; squadId: string; events: SquadAuditEvent[] };


export const INITIAL_STATE: ActiveSquadsState = { bySquadId: {} };


export function reducer(state: ActiveSquadsState, action: Action): ActiveSquadsState {
  switch (action.type) {
    case 'HYDRATE': {
      const next = { ...state.bySquadId };
      for (const s of action.squads) {
        next[s.id] = {
          squad: s,
          members: action.members[s.id] || [],
          lastEventTs: s.dispatched_at,
        };
      }
      return { bySquadId: next };
    }
    case 'WS_EVENT':
      return applyEventToState(state, action.event);
    case 'REPLAY_AUDIT': {
      let next = state;
      for (const e of action.events) {
        next = applyEventToState(next, auditToEventShape(e));
      }
      return next;
    }
    default:
      return state;
  }
}


/**
 * Apply a single chat_squad_* event to the squad-entry map.
 *
 * Sticky-terminal (cycle-2 invariant #3): once a squad or member reaches a
 * terminal status, subsequent events for that target are no-ops (mirrors
 * the backend sqdr_004 trigger).
 *
 * Dedupe by ts (SP3-MF-01): events with ts <= lastEventTs are dropped.
 */
function applyEventToState(state: ActiveSquadsState, event: WsEventShape): ActiveSquadsState {
  const target = event.target || {};
  const squadId = target.squad_id;
  if (!squadId) return state;

  const entry = state.bySquadId[squadId];
  if (!entry) return state;  // squad not in our active set

  const eventTs = event.timestamp || new Date().toISOString();
  if (entry.lastEventTs >= eventTs) return state;

  let nextSquad = entry.squad;
  let nextMembers = entry.members;

  switch (event.event_type) {
    case 'chat_squad_dispatched':
      break;

    case 'chat_squad_member_started':
      nextMembers = _setMemberStatus(entry.members, target.persona_id, 'running', {
        dispatched_at: eventTs,
      });
      break;

    case 'chat_squad_member_completed':
      nextMembers = _setMemberStatus(entry.members, target.persona_id, 'done', {
        completed_at: eventTs,
      });
      break;

    case 'chat_squad_member_failed': {
      const err = target.error || 'failed';
      nextMembers = _setMemberStatus(entry.members, target.persona_id, 'failed', {
        output_summary: `failed: ${err}`,
        completed_at: eventTs,
      });
      break;
    }

    case 'chat_squad_member_timed_out':
      nextMembers = _setMemberStatus(entry.members, target.persona_id, 'timed_out', {
        completed_at: eventTs,
      });
      break;

    case 'chat_squad_member_recalled':
      nextMembers = _setMemberStatus(entry.members, target.persona_id, 'recalled', {
        completed_at: eventTs,
      });
      break;

    case 'chat_squad_member_skipped': {
      const reason = target.skip_reason || 'skipped';
      nextMembers = _setMemberStatus(entry.members, target.persona_id, 'skipped', {
        output_summary: reason,
        completed_at: eventTs,
      });
      break;
    }

    case 'chat_squad_completed':
      if (!TERMINAL_SQUAD_STATES.has(entry.squad.status)) {
        nextSquad = {
          ...entry.squad,
          status: 'completed',
          total_actual_cost_usd: typeof target.total_cost_usd === 'number'
            ? target.total_cost_usd
            : entry.squad.total_actual_cost_usd,
        };
      }
      break;

    case 'chat_squad_recalled':
      if (!TERMINAL_SQUAD_STATES.has(entry.squad.status)) {
        nextSquad = { ...entry.squad, status: 'recalled', recalled_at: eventTs };
      }
      break;

    case 'chat_squad_timed_out':
      if (!TERMINAL_SQUAD_STATES.has(entry.squad.status)) {
        nextSquad = { ...entry.squad, status: 'timed_out' };
      }
      break;

    case 'chat_squad_budget_exceeded':
      if (!TERMINAL_SQUAD_STATES.has(entry.squad.status)) {
        nextSquad = { ...entry.squad, status: 'cost_capped' };
      }
      break;

    case 'chat_squad_stuck':
      // Transient warning — banner UI surfaces via separate channel.
      break;

    default:
      return state;
  }

  return {
    bySquadId: {
      ...state.bySquadId,
      [squadId]: { squad: nextSquad, members: nextMembers, lastEventTs: eventTs },
    },
  };
}


function _setMemberStatus(
  members: SquadMember[],
  personaId: string | undefined,
  newStatus: SquadMemberStatus,
  extra: Partial<SquadMember> = {},
): SquadMember[] {
  if (!personaId) return members;
  return members.map((m) =>
    m.persona_id === personaId && !TERMINAL_MEMBER_STATES.has(m.status)
      ? { ...m, status: newStatus, ...extra }
      : m,
  );
}


/**
 * Map audit-log entry to wire-event shape. Backend audit events use
 * shorter names (e.g. `member_done`) than the bus events
 * (`chat_squad_member_completed`).
 */
function auditToEventShape(audit: SquadAuditEvent): WsEventShape {
  const eventTypeMap: Record<string, string> = {
    'dispatched': 'chat_squad_dispatched',
    'member_started': 'chat_squad_member_started',
    'member_done': 'chat_squad_member_completed',
    'member_failed': 'chat_squad_member_failed',
    'member_timed_out': 'chat_squad_member_timed_out',
    'member_recalled': 'chat_squad_member_recalled',
    'member_skipped': 'chat_squad_member_skipped',
    'completed': 'chat_squad_completed',
    'recalled': 'chat_squad_recalled',
    'timed_out': 'chat_squad_timed_out',
    'cost_capped': 'chat_squad_budget_exceeded',
  };
  return {
    event_type: eventTypeMap[audit.event] || `chat_squad_${audit.event}`,
    timestamp: audit.ts,
    target: { squad_id: audit.squad_id, ...audit.details },
  };
}
